import {
  ReverseVestingScheduleField,
  ReverseVestingScheduleIssue,
  validateReverseVestingSchedule,
} from 'shared';

import { getTranslation } from './get-translation.util';

export const vestingScheduleIssueKey: Record<
  ReverseVestingScheduleIssue,
  Parameters<typeof getTranslation>[0]
> = {
  [ReverseVestingScheduleIssue.CLIFF_EXCEEDS_DURATION]: 'CLIFF_CANNOT_EXCEED_DURATION',
  [ReverseVestingScheduleIssue.CLIFF_NOT_DIVISIBLE_BY_FREQUENCY]:
    'CLIFF_SHOULD_BE_DIVISIBLE_BY_FREQUENCY',
  [ReverseVestingScheduleIssue.DURATION_NOT_DIVISIBLE_BY_FREQUENCY]:
    'DURATION_SHOULD_BE_MULTIPLE_OF_FREQUENCY',
  [ReverseVestingScheduleIssue.FREQUENCY_EXCEEDS_DURATION]: 'FREQUENCY_CANNOT_EXCEED_DURATION',
};

export const VESTING_SCHEDULE_FIELDS: ReverseVestingScheduleField[] = [
  'cliffMonths',
  'durationMonths',
  'frequencyMonths',
];

/**
 * Whether a field error is one of the schedule rules. Those are owned by
 * `getVestingScheduleErrors`, so a copy left in react-hook-form's state is stale by definition —
 * unlike "Required" or "Must be a whole number", which describe the field's own value.
 */
export const isVestingScheduleMessage = (message: unknown): boolean =>
  typeof message === 'string' &&
  Object.values(vestingScheduleIssueKey).some((key) => getTranslation(key) === message);

type ScheduleValue = null | number | string | undefined;

const toScheduleNumber = (value: ScheduleValue): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * The schedule rules as per-field messages, derived from the current values rather than from
 * react-hook-form's error state. The rules are cross-field — typing a frequency makes the
 * *duration* invalid — and in `onChange` mode react-hook-form only refreshes the error of the field
 * being edited, so the resolver's issue on the sibling field never reached the screen.
 */
export const getVestingScheduleErrors = ({
  cliffMonths,
  durationMonths,
  frequencyMonths,
}: {
  cliffMonths?: ScheduleValue;
  durationMonths?: ScheduleValue;
  frequencyMonths?: ScheduleValue;
}): Partial<Record<ReverseVestingScheduleField, string>> =>
  validateReverseVestingSchedule({
    cliffMonths: toScheduleNumber(cliffMonths),
    durationMonths: toScheduleNumber(durationMonths),
    frequencyMonths: toScheduleNumber(frequencyMonths),
  }).reduce<Partial<Record<ReverseVestingScheduleField, string>>>(
    (errors, { field, issue }) =>
      errors[field]
        ? errors
        : { ...errors, [field]: getTranslation(vestingScheduleIssueKey[issue]) },
    {},
  );
